import { useEffect, useState, type ReactNode } from 'react';
import { sessionCompanyId } from '../authProvider';
import { loadCompanyModules, subscribeCompanyModules } from './companyModulesApi';
import { moduleEnabledFor, type CompanyWithModules, type ModuleKey } from './modules';

/**
 * Hak i komponent do ukrywania sekcji modułu wyłączonego spółce (T.01,
 * 06.10.2026). Wzór: `PermissionGate` — bez modułu sekcji nie ma wcale.
 * Reguła w `modules.ts`, dane z `companyModulesApi.ts`.
 */

/** Spółki z modułami; `null`, dopóki nie przyszły (albo gdy odczyt się nie udał). */
export function useCompanyModules(): CompanyWithModules[] | null {
  const [companies, setCompanies] = useState<CompanyWithModules[] | null>(null);
  useEffect(() => {
    let alive = true;
    void loadCompanyModules().then((c) => {
      if (alive) setCompanies(c);
    });
    const unsubscribe = subscribeCompanyModules((c) => {
      if (alive) setCompanies(c);
    });
    return () => {
      alive = false;
      unsubscribe();
    };
  }, []);
  return companies;
}

/**
 * Czy moduł działa w kontekście: spółka rekordu (`companyId`), a bez niej —
 * spółka konta, a bez niej — którakolwiek spółka. Zanim moduły przyjdą — tak.
 */
export function useModuleEnabled(module: ModuleKey, companyId?: number | null): boolean {
  const companies = useCompanyModules();
  return moduleEnabledFor(companies, module, companyId, sessionCompanyId());
}

/** Sekcja widoczna tylko z włączonym modułem. Bez niego — nic. */
export const ModuleGate = ({
  module,
  companyId,
  children,
}: {
  module: ModuleKey;
  /** Spółka rekordu, którego dotyczy sekcja (zlecenie, pojazd). */
  companyId?: number | null;
  children: ReactNode;
}) => (useModuleEnabled(module, companyId) ? <>{children}</> : null);

/** Spółka z rekordu react-admina — liczba albo nic. */
export function companyIdOf(record: Record<string, unknown> | undefined | null): number | null {
  const v = record?.companyId;
  return typeof v === 'number' ? v : null;
}
